<script setup>
import { ref, computed } from 'vue';
import { useBugReport } from '@/composables/useBugReport';
import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

const { isOpen, close, submit } = useBugReport();

// Form state
const description = ref('');
const severity     = ref('');
const category     = ref('');
const steps        = ref('');
const screenshot   = ref(null); // base64 data URL or null
const screenshotSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

// Submit state
const submitting = ref(false);
const error      = ref('');
const taskUrl    = ref('');

const canSubmit = computed(() =>
  description.value.trim() && severity.value && category.value && !submitting.value
);

// Backdrop close
const backdrop = useModalBackdropClose(() => {
  if (!submitting.value) handleClose();
});

function handleClose() {
  close();
  description.value = '';
  severity.value     = '';
  category.value     = '';
  steps.value        = '';
  screenshot.value   = null;
  error.value        = '';
  taskUrl.value      = '';
  submitting.value   = false;
}

async function captureScreenshot() {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    await new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.onplaying = () => {
        const MAX_WIDTH = 1920;
        const scale = Math.min(1, MAX_WIDTH / video.videoWidth);
        const canvas = document.createElement('canvas');
        canvas.width  = Math.round(video.videoWidth  * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        stream.getTracks().forEach(t => t.stop());
        screenshot.value = canvas.toDataURL('image/png');
        resolve();
      };
      video.onerror = (e) => { stream.getTracks().forEach(t => t.stop()); reject(e); };
      video.play().catch(reject);
    });
  } catch (e) {
    if (e.name !== 'NotAllowedError') {
      error.value = 'Screenshot capture failed. You can still submit without one.';
    }
  }
}

async function handleSubmit() {
  if (!canSubmit.value) return;
  submitting.value = true;
  error.value = '';
  try {
    const result = await submit(
      { description: description.value.trim(), severity: severity.value, category: category.value, steps: steps.value.trim() },
      screenshot.value
    );
    taskUrl.value = result.taskUrl;
  } catch (e) {
    error.value = e.message?.includes('500')
      ? 'Could not submit — server error. Please try again.'
      : 'Could not submit — check your connection and try again.';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/50 flex justify-center items-center z-[60] p-4"
      @mousedown.self="backdrop.onBackdropMousedown"
      @mouseup.self="backdrop.onBackdropMouseup"
    >
      <div
        class="ui-modal rounded-lg shadow-xl w-full max-w-lg flex flex-col border border-gray-200 dark:border-gray-600"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        @mousedown="backdrop.onContentMousedown"
      >
        <!-- Success state -->
        <div v-if="taskUrl" class="p-8 flex flex-col items-center text-center gap-4">
          <div class="w-12 h-12 rounded-full bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 class="font-extrabold text-lg text-mc-ink dark:text-on-dark">Report submitted</h2>
          <p class="text-sm text-ink-muted dark:text-on-dark-muted">Your IT team has been notified and will follow up.</p>
          <button
            type="button"
            class="mt-2 text-xs text-ink-muted dark:text-on-dark-muted hover:underline"
            @click="handleClose"
          >
            Close
          </button>
        </div>

        <!-- Form state -->
        <template v-else>
          <div class="p-6 pb-0">
            <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
            <div class="flex justify-between items-center mb-1">
              <h2 id="bug-report-title" class="font-extrabold text-xl text-mc-ink dark:text-on-dark tracking-tight">
                Report a Problem
              </h2>
              <button
                type="button"
                :disabled="submitting"
                class="w-7 h-7 rounded-full bg-mc-cloud dark:bg-white/10 flex items-center justify-center text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel dark:hover:bg-white/20 transition-colors duration-160 disabled:opacity-50"
                @click="handleClose"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <p class="text-xs text-ink-muted dark:text-on-dark-muted mb-5">
              Page, logs, and app state are captured automatically. Just describe what happened.
            </p>
          </div>

          <div class="px-6 pb-2 flex flex-col gap-4 overflow-y-auto">
            <!-- Description -->
            <div>
              <label class="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1">
                What happened? <span class="text-mc-red">*</span>
              </label>
              <textarea
                v-model="description"
                rows="3"
                placeholder="Describe the problem..."
                class="w-full rounded-lg border border-mc-steel dark:border-white/20 bg-white dark:bg-white/5 text-mc-ink dark:text-on-dark text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-mc-blue placeholder:text-ink-muted/50"
              />
            </div>

            <!-- Severity + Category -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1">
                  Severity <span class="text-mc-red">*</span>
                </label>
                <select
                  v-model="severity"
                  class="w-full rounded-lg border border-mc-steel dark:border-white/20 bg-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mc-blue"
                >
                  <option value="" disabled>Select...</option>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div>
                <label class="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1">
                  Category <span class="text-mc-red">*</span>
                </label>
                <select
                  v-model="category"
                  class="w-full rounded-lg border border-mc-steel dark:border-white/20 bg-white dark:bg-mc-charcoal text-mc-ink dark:text-on-dark text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mc-blue"
                >
                  <option value="" disabled>Select...</option>
                  <option>Bug</option>
                  <option>Performance</option>
                  <option>UI Issue</option>
                  <option>Data Issue</option>
                  <option>Feature Request</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            <!-- Steps to reproduce -->
            <div>
              <label class="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1">
                Steps to Reproduce <span class="text-[11px] font-normal normal-case tracking-normal">(optional)</span>
              </label>
              <textarea
                v-model="steps"
                rows="2"
                placeholder="1. Go to...  2. Click..."
                class="w-full rounded-lg border border-mc-steel dark:border-white/20 bg-white dark:bg-white/5 text-mc-ink dark:text-on-dark text-sm px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-mc-blue placeholder:text-ink-muted/50"
              />
            </div>

            <!-- Screenshot -->
            <div v-if="screenshotSupported">
              <label class="block text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-muted dark:text-on-dark-muted mb-1">
                Screenshot <span class="text-[11px] font-normal normal-case tracking-normal">(optional)</span>
              </label>
              <div v-if="screenshot" class="relative">
                <img :src="screenshot" alt="Screenshot preview" class="w-full rounded-lg border border-mc-steel dark:border-white/20 max-h-32 object-cover object-top" />
                <button
                  type="button"
                  class="absolute top-1 right-1 w-6 h-6 bg-black/60 text-white rounded-full text-xs flex items-center justify-center hover:bg-black/80"
                  @click="screenshot = null"
                  aria-label="Remove screenshot"
                >
                  &times;
                </button>
              </div>
              <button
                v-else
                type="button"
                class="w-full border-2 border-dashed border-mc-steel dark:border-white/20 rounded-lg px-3 py-3 text-sm text-ink-muted dark:text-on-dark-muted bg-mc-cloud/50 dark:bg-white/5 hover:border-mc-blue dark:hover:border-mc-blue hover:text-mc-blue transition-colors duration-160 flex items-center justify-center gap-2"
                @click="captureScreenshot"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                Capture Screenshot
              </button>
            </div>

            <!-- Auto-captured notice -->
            <div class="flex gap-2 items-start bg-mc-cloud dark:bg-white/5 rounded-lg px-3 py-2">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="flex-shrink-0 mt-0.5 text-ink-muted dark:text-on-dark-muted" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span class="text-[11px] text-ink-muted dark:text-on-dark-muted leading-relaxed">
                Also included automatically: current page and route, recent console errors, app state, your account info, and browser details.
              </span>
            </div>

            <!-- Error message -->
            <p v-if="error" class="text-xs text-red-600 dark:text-red-400">{{ error }}</p>
          </div>

          <!-- Footer -->
          <div class="p-6 pt-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              :disabled="submitting"
              class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160 disabled:opacity-50 disabled:pointer-events-none"
              @click="handleClose"
            >
              Cancel
            </button>
            <button
              type="button"
              :disabled="!canSubmit"
              class="default-button inline-flex items-center gap-2"
              @click="handleSubmit"
            >
              <span
                v-if="submitting"
                class="inline-block size-4 border-2 border-current border-t-transparent rounded-full animate-spin"
                aria-hidden="true"
              />
              {{ submitting ? 'Sending...' : 'Send Report' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>
